<template>
  <div id="admin-dashboard" data-test-id="admin-dashboard" class="flex flex-col gap-4">
    <div v-if="data" class="flex flex-col gap-1">
      <Button
        v-if="view === 'program' && hasOverview"
        variant="ghost"
        size="sm"
        class="-ml-2 w-fit text-muted-foreground"
        @click="loadOverview"
      >
        <ArrowLeft class="size-4" aria-hidden="true" />
        Tous les programmes
      </Button>
      <h1 class="text-xl font-semibold">{{ data.title }}</h1>
      <p class="text-sm text-muted-foreground">
        {{ subtitle }}
      </p>
      <p
        v-if="data.scope === 'MANAGED_GROUPS' && hiddenCount > 0 && data.tabs.length > 0"
        class="text-sm text-muted-foreground"
      >
        Graphiques masqués : {{ hiddenCount }}. Ils portent sur tout le programme et ne peuvent pas être limités à vos
        groupes.
      </p>
    </div>
    <div v-if="loading" class="flex flex-col gap-4" aria-busy="true">
      <Skeleton class="h-8 w-72" />
      <div class="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Skeleton v-for="n in 4" :key="n" class="h-24 rounded-xl" />
      </div>
      <Skeleton class="h-40 rounded-xl" />
      <Skeleton class="h-72 rounded-xl" />
    </div>
    <p v-if="errorMessage" class="text-sm text-destructive">{{ errorMessage }}</p>

    <Tabs v-if="data && !isEmpty" v-model="activeTab" class="flex flex-col gap-4">
      <TabsList v-if="data.tabs.length > 1">
        <TabsTrigger v-for="tab in data.tabs" :key="tab.title" :value="tab.title">{{ tab.title }}</TabsTrigger>
      </TabsList>

      <TabsContent v-for="tab in data.tabs" :key="tab.title" :value="tab.title" class="flex flex-col gap-4">
        <ProgramsOverview v-if="programsChart(tab.charts)" :chart="programsChart(tab.charts)!" @open="openProgram" />
        <div
          class="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
          v-if="!programsChart(tab.charts) && kpis(tab.charts).length"
        >
          <Card v-for="kpi in kpis(tab.charts)" :key="kpi.id" class="gap-0 py-4 sm:py-6">
            <CardHeader class="px-4 sm:px-6">
              <CardDescription>{{ kpiLabel(kpi.title) }}</CardDescription>
              <CardTitle class="text-2xl tabular-nums sm:text-3xl">{{ formatKpi(kpi) }}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <div v-if="!programsChart(tab.charts)" class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <!-- The program health, once, in place of the sante_* charts. -->
          <HealthCards v-if="health && tab.title === healthTab" :health="health" @open-list="openList" />
          <Card
            v-for="chart in others(tab.charts)"
            :id="chart.key && isPeopleList(chart) ? listAnchor(chart.key) : undefined"
            :key="chart.id"
            class="min-w-0 scroll-mt-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            :class="['pie', 'progress'].includes(render(chart)) ? '' : 'lg:col-span-2'"
            :tabindex="chart.key && isPeopleList(chart) ? -1 : undefined"
          >
            <CardHeader>
              <div class="flex items-center gap-1">
                <CardTitle class="text-base">{{ titleOf(chart) }}</CardTitle>
                <InfoHint v-if="chart.description" :label="titleOf(chart)" :text="chart.description" />
                <Button
                  v-if="isPeopleList(chart) && chart.rows.length > 0"
                  variant="outline"
                  size="sm"
                  class="ml-auto"
                  :aria-label="`Exporter la liste ${titleOf(chart)}`"
                  @click="exportList(chart)"
                >
                  <Download class="size-4" aria-hidden="true" />
                  Exporter
                </Button>
              </div>
              <CardDescription v-if="render(chart) === 'table' && !chart.error">{{
                listSummary(chart)
              }}</CardDescription>
            </CardHeader>
            <CardContent>
              <p v-if="chart.error" class="text-sm text-muted-foreground">
                Ce graphique est indisponible pour le moment.
              </p>
              <ManagerCards v-else-if="render(chart) === 'managers'" :chart="chart" />
              <div v-else-if="render(chart) === 'progress'" class="flex flex-col gap-3">
                <div class="relative h-3 rounded-full bg-muted">
                  <div
                    class="absolute inset-y-0 left-0 rounded-full bg-primary"
                    :style="{ width: `${progress(chart).done}%` }"
                  />
                  <div
                    v-if="progress(chart).elapsed !== null"
                    class="absolute -top-1 h-5 w-0.5 bg-foreground"
                    :style="{ left: `${progress(chart).elapsed}%` }"
                  />
                </div>
                <div class="flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
                  <span>{{ formatPct(progress(chart).done) }} du parcours réalisé</span>
                  <span v-if="progress(chart).elapsed !== null">
                    Repère : {{ formatPct(progress(chart).elapsed) }} du temps écoulé
                  </span>
                </div>
              </div>
              <ul v-else-if="render(chart) === 'ranking'" class="flex flex-col gap-3">
                <li
                  v-for="item in ranking(chart)"
                  :key="item.label"
                  class="grid grid-cols-1 items-center gap-x-4 gap-y-1 text-sm sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_6rem]"
                >
                  <span class="min-w-0">{{ item.label }}</span>
                  <span class="h-2 rounded-sm bg-muted">
                    <span class="block h-full rounded-sm bg-primary" :style="{ width: `${item.share}%` }" />
                  </span>
                  <span class="tabular-nums text-muted-foreground sm:text-right">{{ item.text }}</span>
                </li>
              </ul>
              <template v-else-if="render(chart) === 'pie'">
                <div class="flex flex-col items-center gap-4">
                  <ChartContainer :config="pie(chart).config" class="aspect-square h-[220px]">
                    <VisSingleContainer :data="pie(chart).data" :margin="{ top: 10, bottom: 10 }">
                      <VisDonut
                        :value="(d: DonutDatumType) => d.value"
                        :color="(d: DonutDatumType) => pie(chart).config[d.key]?.color"
                        :arc-width="55"
                        :central-label="pie(chart).total.toLocaleString('fr-FR')"
                        central-label-text-color="var(--muted-foreground)"
                      />
                      <ChartTooltip
                        :triggers="{ [Donut.selectors.segment]: donutTooltip(pie(chart).config, pie(chart).total) }"
                      />
                    </VisSingleContainer>
                  </ChartContainer>
                  <ul class="grid w-full gap-1 text-sm">
                    <li v-for="d in pie(chart).data" :key="d.key" class="flex items-center justify-between">
                      <span class="flex items-center gap-2">
                        <span
                          class="inline-block size-2.5 rounded-full"
                          :style="{ background: pie(chart).config[d.key]?.color }"
                        />
                        {{ pie(chart).config[d.key]?.label }}
                      </span>
                      <span class="tabular-nums text-muted-foreground">{{ d.value.toLocaleString('fr-FR') }}</span>
                    </li>
                  </ul>
                </div>
              </template>

              <XyChart
                v-else-if="render(chart) === 'line' || render(chart) === 'bar'"
                :chart="chart"
                :kind="render(chart) === 'line' ? 'line' : 'bar'"
              />
              <template v-else>
                <ul
                  v-if="isPeopleList(chart)"
                  class="flex max-h-96 flex-col overflow-y-auto rounded-md border md:hidden"
                  :aria-label="titleOf(chart)"
                >
                  <li
                    v-for="(row, index) in chart.rows"
                    :key="index"
                    class="flex flex-col gap-0.5 px-3 py-2.5 text-sm even:bg-muted/60"
                  >
                    <span class="font-medium">{{ formatCell(row.nom) }}</span>
                    <span class="truncate text-muted-foreground">{{ formatCell(row.email) }}</span>
                    <span class="text-xs text-muted-foreground">{{ personDetails(row) }}</span>
                  </li>
                  <li v-if="chart.rows.length === 0" class="px-3 py-6 text-center text-sm text-muted-foreground">
                    Personne pour l'instant
                  </li>
                </ul>
                <div
                  :class="isPeopleList(chart) ? 'hidden md:block' : ''"
                  class="[&>[data-slot=table-container]]:max-h-96"
                >
                  <Table>
                    <TableHeader class="sticky top-0 z-10 bg-muted shadow-[inset_0_-1px_0_var(--border)]">
                      <TableRow class="border-b-0 hover:bg-transparent">
                        <TableHead
                          v-for="col in visibleColumns(chart)"
                          :key="col"
                          class="whitespace-nowrap text-xs font-semibold text-muted-foreground"
                          >{{ columnLabel(col) }}</TableHead
                        >
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow
                        v-for="(row, index) in chart.rows"
                        :key="index"
                        class="border-b-0 even:bg-muted/60 hover:bg-accent"
                      >
                        <TableCell
                          v-for="(col, colIndex) in visibleColumns(chart)"
                          :key="col"
                          class="whitespace-nowrap py-2.5"
                          :class="colIndex === 0 ? 'font-medium' : col === 'email' ? 'text-muted-foreground' : ''"
                          >{{ formatCell(row[col]) }}</TableCell
                        >
                      </TableRow>
                      <TableEmpty v-if="chart.rows.length === 0" :colspan="visibleColumns(chart).length || 1"
                        >Personne pour l'instant</TableEmpty
                      >
                    </TableBody>
                  </Table>
                </div>
              </template>
            </CardContent>
          </Card>
        </div>
      </TabsContent>
    </Tabs>

    <Card v-else-if="data && data.tabs.length === 0 && hiddenCount > 0">
      <CardHeader>
        <CardTitle class="text-base">Aucun graphique disponible pour vos groupes</CardTitle>
        <CardDescription>
          Les graphiques de ce tableau de bord portent sur tout le programme : ils ne peuvent pas être limités aux
          groupes que vous suivez.
        </CardDescription>
      </CardHeader>
    </Card>
    <Card v-else-if="data && isEmpty">
      <CardHeader>
        <CardTitle class="text-base">Aucune donnée pour ce programme</CardTitle>
        <CardDescription>
          Ce tableau de bord ne contient rien pour le programme sélectionné. Choisissez un autre programme dans le
          sélecteur en haut à droite.
        </CardDescription>
      </CardHeader>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { Donut } from '@unovis/ts'
import { VisDonut, VisSingleContainer } from '@unovis/vue'
import { ArrowLeft, Download } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { DashboardChartType, V1GetDashboardResponseType } from '@/features/admin/analytics/api/dashboard'
import { V1GetDashboard, V1GetDashboardOverview } from '@/features/admin/analytics/api/dashboard'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import {
  buildPie,
  type DonutDatumType,
  donutTooltip,
  xyFits,
} from '@/features/ui/pages/admin/dashboard/dashboard-content/chart-helpers'
import { downloadCsv, toCsv } from '@/features/ui/pages/admin/dashboard/dashboard-content/export-csv'
import HealthCards from '@/features/ui/pages/admin/dashboard/dashboard-content/health-cards.vue'
import InfoHint from '@/features/ui/pages/admin/dashboard/dashboard-content/info-hint.vue'
import ManagerCards from '@/features/ui/pages/admin/dashboard/dashboard-content/manager-cards.vue'
import {
  buildProgramHealth,
  healthChartApplies,
  isHealthChart,
  listApplies,
  listForAdminsOnly,
  listsObjective,
  listSummary,
  listTitle,
  sortLists,
} from '@/features/ui/pages/admin/dashboard/dashboard-content/program-health'
import { LEVELS_ON } from '@/features/ui/pages/admin/dashboard/dashboard-content/program-health-levels'
import ProgramsOverview from '@/features/ui/pages/admin/dashboard/dashboard-content/programs-overview.vue'
import XyChart from '@/features/ui/pages/admin/dashboard/dashboard-content/xy-chart.vue'

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const data = ref<V1GetDashboardResponseType | null>(null)
const loading = ref(false)
const errorMessage = ref<string | null>(null)

// « Tous les programmes » first when the person is admin or manager of several programs, then one program.
const view = ref<'overview' | 'program'>('overview')
const counts = ref<{ programCount: number; adminCount: number; managerCount: number } | null>(null)
const hasOverview = ref(false)
// Only the latest request fills the page: a slow answer must not overwrite the view chosen since.
let requestSeq = 0

async function load() {
  const seq = ++requestSeq
  view.value = 'program'
  errorMessage.value = null
  data.value = null
  const programId = selectedProgramIdRef.value
  if (!programId) return
  loading.value = true
  try {
    const result = await V1GetDashboard(programId)
    if (seq === requestSeq) data.value = result
  } catch {
    if (seq === requestSeq) errorMessage.value = 'Le tableau de bord est indisponible pour le moment.'
  } finally {
    if (seq === requestSeq) loading.value = false
  }
}

async function loadOverview() {
  const seq = ++requestSeq
  view.value = 'overview'
  errorMessage.value = null
  data.value = null
  loading.value = true
  let dashboard: V1GetDashboardResponseType | null = null
  try {
    const result = await V1GetDashboardOverview()
    counts.value = result
    dashboard = result.dashboard
  } catch {
    dashboard = null
  }
  if (seq !== requestSeq) return
  loading.value = false
  hasOverview.value = !!dashboard
  // A single program, or no overview on this platform yet: straight to the program.
  if (dashboard) data.value = dashboard
  else await load()
}

function openProgram(programId: string) {
  if (selectedProgramIdRef.value === programId) void load()
  else programsStore.setSelectedProgramId(programId)
}

const subtitle = computed(() => {
  if (!data.value) return ''
  if (data.value.scope === 'PROGRAM') return 'Vue administrateur : tout le programme'
  if (data.value.scope === 'MANAGED_GROUPS') return 'Vue manager : vos groupes uniquement'
  const { programCount = 0, adminCount = 0, managerCount = 0 } = counts.value ?? {}
  if (managerCount === 0) return `Vue administrateur : vos ${programCount} programmes`
  if (adminCount === 0) return `Vue manager : vos groupes, dans ${programCount} programmes`
  return `Vos ${programCount} programmes : ${adminCount} en administrateur, ${managerCount} en manager (vos groupes uniquement)`
})

// A program outside the dashboard scope answers with empty rows or zeros everywhere: say it instead of showing a wall of dashes.
// A chart in error has no rows either, but it is unavailable, not empty: it keeps the page on « indisponible ».
const isEmpty = computed(
  () =>
    !!data.value &&
    data.value.tabs.every((tab) =>
      tab.charts.every(
        (chart) => !chart.error && chart.rows.every((row) => Object.values(row).every((v) => v === null || v === 0)),
      ),
    ),
)

const cleanTitle = (title: string) => title.replace(/^MDIV - /, '')
const kpiValue = (chart: DashboardChartType) => {
  const row = chart.rows[0]
  const value = row ? Object.values(row)[0] : null
  return typeof value === 'number' ? value : null
}
// A KPI without a value (e.g. a satisfaction score under 5 reviews) or at zero, and a chart without enough rows,
// are left out: the page shows what the program produced, not empty boxes.
const kpis = (charts: DashboardChartType[]) =>
  charts.filter((c) => c.viz === 'big_number_total' && !c.error && !!kpiValue(c))
const others = (charts: DashboardChartType[]) => sortLists(charts.filter(isShown))
function isShown(c: DashboardChartType) {
  // The « À relancer » lists that do not apply to the program objective are left out, and for a manager those of the
  // requesting mentees, who have no group (always empty): neither counts as a hidden chart.
  if (!listApplies(c.key, objective.value)) return false
  if (data.value?.scope === 'MANAGED_GROUPS' && listForAdminsOnly(c.key)) return false
  // A health chart in error that feeds no card of the objective is not announced as missing.
  if (isHealthChart(c) && !healthChartApplies(c.key, objective.value)) return false
  if (c.error) return true
  // Drawn by the health cards.
  if (isHealthChart(c)) return false
  if (c.viz === 'big_number_total') return false
  if (render(c) === 'progress') return progress(c).done !== null
  // Shown only when at least one pair of the program has a manager.
  if (render(c) === 'managers') return c.rows.some((row) => !row.sans_manager)
  // A curve needs at least two points to show a trend.
  if (render(c) === 'line') return c.rows.length > 1
  // An empty list of people is good news: it stays, with its empty state.
  if (render(c) === 'table') return true
  return c.rows.length > 0
}
const kpiLabel = (title: string) => cleanTitle(title).replace(/ [(](sur [0-9]+|%)[)]$/, '')
// groupe_id only counts the groups of a list: neither shown nor exported.
const visibleColumns = (chart: DashboardChartType) =>
  chart.columns.filter((c) => !c.endsWith('__contribution') && c !== 'groupe_id')
const COLUMN_LABELS: Record<string, string> = {
  nom: 'Nom',
  email: 'E-mail',
  role: 'Rôle',
  binome: 'Groupe',
  depuis: 'Depuis le',
}
const columnLabel = (col: string) => COLUMN_LABELS[col] ?? col

// The lists of people (« À relancer ») can be exported, the other tables cannot.
const isPeopleList = (chart: DashboardChartType) => render(chart) === 'table' && chart.columns.includes('nom')
// The second line of a person on a phone: role, pair and date, whichever are known.
const personDetails = (row: DashboardChartType['rows'][number]) =>
  [row.role, row.binome && row.binome !== '—' ? row.binome : null, row.depuis ? `depuis le ${row.depuis}` : null]
    .filter(Boolean)
    .join(' · ')
function exportList(chart: DashboardChartType) {
  const columns = visibleColumns(chart)
  downloadCsv(titleOf(chart), toCsv(columns, columnLabel, chart.rows))
}

function formatCell(value: string | number | boolean | null | undefined) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number')
    return Number.isInteger(value) ? value.toLocaleString('fr-FR') : value.toFixed(1).replace('.', ',')
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non'
  return String(value)
}

const formatPct = (value: number | null) => (value === null ? '—' : `${Math.round(value).toLocaleString('fr-FR')} %`)

// The unit is written in the chart title in Superset: « (sur 5) », « (sur 20) », « (%) », « Heures ».
function formatKpi(chart: DashboardChartType) {
  const value = kpiValue(chart)
  if (value === null) return '—'
  const outOf = chart.title.match(/[(]sur ([0-9]+)[)]/)
  if (outOf) return `${value.toFixed(1).replace('.', ',')} / ${outOf[1]}`
  if (chart.title.includes('%')) return formatPct(value)
  if (/heures/i.test(chart.title)) return `${Math.round(value).toLocaleString('fr-FR')} h`
  return Math.round(value).toLocaleString('fr-FR')
}

// « Avancement » charts answer one row: share of the expected sessions held, share of the program time elapsed.
function progress(chart: DashboardChartType) {
  const row = chart.rows[0] ?? {}
  const pct = (v: unknown) => (typeof v === 'number' ? Math.max(0, Math.min(100, v)) : null)
  return { done: pct(row.avancement_pct), elapsed: pct(row.temps_ecoule_pct) }
}

// A bar chart over categories (not dates) reads better as a ranked list with long labels.
function ranking(chart: DashboardChartType) {
  const [labelCol, valueCol] = chart.columns
  const items = chart.rows.map((row) => ({ label: String(row[labelCol] ?? '—'), value: Number(row[valueCol] ?? 0) }))
  const max = Math.max(1, ...items.map((i) => i.value))
  return items.map((i) => ({
    ...i,
    share: (i.value / max) * 100,
    text: `${i.value.toLocaleString('fr-FR')} ${String(valueCol).toLowerCase()}`,
  }))
}

const pie = buildPie

// How a chart is drawn. Anything else, or a line/bar chart with too many series, is shown as a table.
function render(
  chart: DashboardChartType,
): 'pie' | 'line' | 'bar' | 'table' | 'progress' | 'ranking' | 'managers' | 'programs' {
  if (
    chart.columns.includes('programme_id') &&
    (chart.columns.includes('avancement_pct') || chart.columns.includes('objectif'))
  )
    return 'programs'
  if (chart.columns.includes('manager_nom')) return 'managers'
  if (chart.columns.includes('avancement_pct')) return 'progress'
  if (chart.viz === 'pie') return 'pie'
  if (chart.viz === 'echarts_timeseries_line' && xyFits(chart)) return 'line'
  const x = chart.rows[0]?.[chart.columns[0]]
  if (chart.viz === 'echarts_timeseries_bar' && chart.columns.length === 2 && typeof x === 'string') return 'ranking'
  if (chart.viz === 'echarts_timeseries_bar' && xyFits(chart)) return 'bar'
  return 'table'
}

// The « Tous les programmes » table: shown with its own filter and figures.
const programsChart = (charts: DashboardChartType[]) => charts.find((c) => !c.error && render(c) === 'programs')

const hiddenCount = computed(() => data.value?.hiddenCharts ?? 0)

// Program health (sante_* charts). A dashboard without them (v1) has none: the page is the same as before.
const allCharts = computed(() => data.value?.tabs.flatMap((tab) => tab.charts) ?? [])
const health = computed(() => (data.value ? buildProgramHealth(allCharts.value, LEVELS_ON) : null))
// The objective the « À relancer » lists are filtered on.
const objective = computed(() => listsObjective(allCharts.value, health.value))
const titleOf = (chart: DashboardChartType) => cleanTitle(listTitle(chart, health.value))
const healthTab = computed(() => data.value?.tabs.find((tab) => tab.charts.some(isHealthChart))?.title ?? null)

// The tab shown: the first one of each dashboard, or « À relancer » from a « Voir la liste » of the health cards.
const activeTab = ref<string>()
watch(data, (value) => (activeTab.value = value?.tabs[0]?.title), { immediate: true })

const listAnchor = (key: string) => `liste-${key}`
async function openList(key: string) {
  const tab = data.value?.tabs.find((t) => t.charts.some((c) => c.key === key))
  if (!tab) return
  activeTab.value = tab.title
  // The tab panel is shown a little after the switch (reka Presence): the list gets the focus once displayed.
  for (let frame = 0; frame < 20; frame++) {
    await new Promise<number>((resolve) => requestAnimationFrame(resolve))
    const card = document.getElementById(listAnchor(key))
    if (card?.offsetParent) return card.focus()
  }
}

onMounted(loadOverview)
// Another program picked (header selector, or a row of the overview): its view. The store filling in the first
// program at start-up does not leave the overview.
watch(selectedProgramIdRef, (id, previous) => {
  if (previous === null && view.value === 'overview') return
  void load()
})
</script>
